

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Modal,
  Snackbar,
  Alert,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import S3FileUpload from 'utils/S3';
import colors from "assets/theme/base/colors";
import { useAuth } from 'auth/Auth';

const AttachmentUploadModal = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  folderPath = 'uploads',
  scope = 'user',
  allowedFileTypes = "*/*",
  maxSizeInMB = 5
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [hasSelectedFile, setHasSelectedFile] = useState(false);
  const uploadRef = useRef();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Reset file selection state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasSelectedFile(false);
    }
  }, [isOpen]);

  const handleUploadClick = async () => {
    if (uploadRef.current) {
      setIsLoading(true);
      try {
        await uploadRef.current.handleUpload();
      } catch (error) {
        console.error('Error initiating upload:', error);
        handleUploadError(error);
      }
    }
  };

  const handleUploadSuccess = async (data) => {
    try {
      await onUpload(data);
      onClose();
    } catch (error) {
      console.error('Error processing upload:', error);
      handleUploadError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadError = (error) => {
    console.error('Upload error:', error);
    setIsLoading(false);
    setSnackbar({
      open: true,
      message: error?.message || 'Error uploading file. Please try again.',
      severity: 'error',
    });
  };

  const handleFileSelect = (file) => {
    setHasSelectedFile(!!file);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        aria-labelledby="attachment-upload-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header - Fixed position */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography 
              id="attachment-upload-modal-title" 
              variant="h6" 
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Upload Attachment
            </MDTypography>
          </Box>

          {/* Scrollable content area */}
          <Box 
            sx={{ 
              overflowY: 'auto',
              p: 4,
              '&::-webkit-scrollbar': {
                width: '0.4em'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555'
              }
            }}
          >
            <MDBox mb={3}>
              <S3FileUpload
                ref={uploadRef}
                folderPath={folderPath}
                tenant={window.location.hostname.split('.')[0]}
                user={user}
                scope={scope}
                env={process.env.REACT_APP_STORAGE_ENV}
                allowedFileTypes={allowedFileTypes}
                maxSizeInMB={maxSizeInMB}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
                onFileSelect={handleFileSelect}
              />
            </MDBox>

            <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
              <MDButton 
                onClick={onClose} 
                color="secondary"
                variant="gradient" 
                sx={{ marginRight: hasSelectedFile ? 2 : 0 }}
                disabled={isLoading}
              >
                Cancel
              </MDButton>
              {hasSelectedFile && (
                <MDButton
                  onClick={handleUploadClick}
                  color="info"
                  variant="gradient"
                  disabled={isLoading}
                >
                  Upload
                </MDButton>
              )}
            </MDBox>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

AttachmentUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  folderPath: PropTypes.string,
  scope: PropTypes.oneOf(['user', 'org']),
  allowedFileTypes: PropTypes.string,
  maxSizeInMB: PropTypes.number
};

export default AttachmentUploadModal;